import { Box, IconButton, Tooltip, Typography } from '@material-ui/core'
import { GetApp } from '@material-ui/icons'
import { clsx } from 'clsx'
import { ChevronDown, Paperclip, SmileIcon } from 'lucide-react'
import { useCallback } from 'react'
import WhatsMarked from 'react-whatsmarked'
import ModalImageCors from '../../components/ModalImageCors'
import { useStyles } from './style'
const ChatMessage = ({
  message,
  currentUser,
  isGroupChat,
  chatTitle,
  hoveredMessageId,
  handleMouseEnter,
  handleMouseLeave,
  handleOpenActionsMenu,
  handleOpenReactionsFromActions,
  datetimeToClient
}) => {
  const classes = useStyles()

  const isSentByMe = message.senderId === currentUser.id
  const senderName = isSentByMe
    ? currentUser.name
    : isGroupChat
    ? message.sender?.name || 'Usuário'
    : chatTitle

  const checkQuotedMessage = useCallback(
    quotedMsg => {
      if (!quotedMsg) return null
      return (
        <div
          className={classes.quotedMessage}
          onClick={e => {
            e.stopPropagation()
            const messageElement = document.getElementById(
              `message-${quotedMsg.id}`
            )
            if (messageElement) {
              messageElement.scrollIntoView({ behavior: 'smooth' })
            }
          }}
        >
          <div className={classes.quotedMessageSender}>
            {quotedMsg.sender?.name || 'Mensagem citada'}
          </div>
          {quotedMsg.mediaPath ? (
            <Paperclip size={14} />
          ) : (
            <WhatsMarked className={classes.messageText}>
              {quotedMsg.message}
            </WhatsMarked>
          )}
        </div>
      )
    },
    [classes]
  )

  const checkMessageMedia = useCallback(
    msg => {
      if (!msg.mediaPath) return null

      if (msg.mediaType === 'image') {
        return <ModalImageCors imageUrl={msg.mediaPath} />
      }
      if (msg.mediaType === 'audio') {
        return (
          <audio controls className={classes.audioContainer}>
            <source src={msg.mediaPath} type="audio/ogg"></source>
          </audio>
        )
      }
      if (msg.mediaType === 'video') {
        return (
          <video
            className={classes.messageMedia}
            src={msg.mediaPath}
            controls
          />
        )
      } else {
        return (
          <div className={classes.downloadMedia}>
            <IconButton color="primary" target="_blank" href={msg.mediaPath}>
              <GetApp />
            </IconButton>
          </div>
        )
      }
    },
    [classes]
  )

  const renderReplies = useCallback(
    (replies, fromMe) => {
      if (!replies || replies.length === 0) {
        return null
      }

      const reactionReplies = replies.filter(reply => {
        const isLikelyEmoji =
          reply?.message && reply.message.length <= 4 && reply?.sender
        return isLikelyEmoji
      })

      if (reactionReplies.length === 0) {
        return null
      }

      const groupedReactions = reactionReplies.reduce((acc, reply) => {
        const emoji = reply.message
        if (!acc[emoji]) {
          acc[emoji] = {
            emoji: emoji,
            count: 0,
            users: []
          }
        }
        acc[emoji].count++
        if (reply.sender?.name) {
          acc[emoji].users.push(reply.sender.name)
        }
        return acc
      }, {})

      const sortedReactions = Object.values(groupedReactions).sort(
        (a, b) => b.count - a.count
      )

      return (
        <div
          className={clsx(
            classes.reactionsContainer,
            fromMe
              ? classes.reactionsContainerRight
              : classes.reactionsContainerLeft
          )}
        >
          <div
            className={clsx(
              classes.reactions,
              fromMe ? classes.reactionsRight : classes.reactionsLeft
            )}
          >
            {sortedReactions.map((reaction, index) => (
              <Tooltip
                key={index}
                title={
                  reaction.users.length > 0
                    ? reaction.users.join(', ')
                    : `${reaction.count} ${
                        reaction.count > 1 ? 'reações' : 'reação'
                      }`
                }
                placement="top"
                arrow
              >
                <div className={classes.reactionItem}>
                  <span>{reaction.emoji}</span>
                  {reaction.count > 1 && (
                    <span className={classes.reactionCount}>
                      {reaction.count}
                    </span>
                  )}
                </div>
              </Tooltip>
            ))}
          </div>
        </div>
      )
    },
    [classes]
  )

  return (
    <div
      id={`message-${message.id}`}
      key={`message-${message.id}`}
      className={clsx(
        classes.boxMessageContainer,
        message.replies &&
          message.replies.length > 0 &&
          classes.messageContainerWithReactions
      )}
      onMouseEnter={() => handleMouseEnter(message.id)}
      onMouseLeave={handleMouseLeave}
    >
      {isSentByMe ? (
        <div className={classes.containerBoxRight}>
          {hoveredMessageId === message.id && (
            <div className={classes.reactionContainer}>
              <IconButton
                className={classes.reactionButton}
                onClick={e => {
                  e.stopPropagation()
                  const rect = e.currentTarget.getBoundingClientRect()
                  const menuPosition = {
                    mouseX: rect.right + 10,
                    mouseY: rect.top - 5
                  }
                  handleOpenReactionsFromActions(menuPosition, message)
                }}
              >
                <SmileIcon size={16} />
              </IconButton>
            </div>
          )}
          <Box className={classes.boxRight}>
            <div className={classes.menuContainer}>
              <Typography variant="subtitle2" className={classes.nameSender}>
                {senderName}
              </Typography>
              <IconButton
                className={classes.menuActionButton}
                onClick={e => {
                  e.stopPropagation()
                  handleOpenActionsMenu(e, message)
                }}
                aria-label="opções da mensagem"
              >
                <ChevronDown size={18} />
              </IconButton>
            </div>
            {message.quotedMessage && checkQuotedMessage(message.quotedMessage)}
            {message.mediaPath && checkMessageMedia(message)}
            {(!message.mediaPath || message.mediaType !== 'audio') && (
              <span className={classes.messageText}>
                {message.message === 'blob' ? '' : message.message}
              </span>
            )}
            {renderReplies(
              message.replies,
              message.senderId === currentUser.id
            )}
            <div className={classes.timestamp}>
              <Typography variant="caption">
                {datetimeToClient(message.createdAt)}
              </Typography>
            </div>
          </Box>
        </div>
      ) : (
        <div className={classes.containerBoxLeft}>
          <Box className={classes.boxLeft}>
            <div className={classes.menuContainer}>
              <Typography variant="subtitle2" className={classes.nameSender}>
                {senderName}
              </Typography>
              <IconButton
                className={classes.menuActionButton}
                onClick={e => {
                  e.stopPropagation()
                  handleOpenActionsMenu(e, message)
                }}
                aria-label="opções da mensagem"
              >
                <ChevronDown size={18} />
              </IconButton>
            </div>
            {message.quotedMessage && checkQuotedMessage(message.quotedMessage)}
            {message.mediaPath && checkMessageMedia(message)}
            <WhatsMarked className={classes.messageText}>
              {message.message === 'blob' ? '' : message.message}
            </WhatsMarked>
            {renderReplies(
              message.replies,
              message.senderId === currentUser.id
            )}
            <div className={classes.timestamp}>
              <Typography variant="caption">
                {datetimeToClient(message.createdAt)}
              </Typography>
            </div>
          </Box>
          {hoveredMessageId === message.id && (
            <div className={classes.reactionContainer}>
              <IconButton
                className={classes.reactionButton}
                onClick={e => {
                  e.stopPropagation()
                  const rect = e.currentTarget.getBoundingClientRect()
                  const menuPosition = {
                    mouseX: rect.right + 10,
                    mouseY: rect.top - 5
                  }
                  handleOpenReactionsFromActions(menuPosition, message)
                }}
              >
                <SmileIcon size={16} />
              </IconButton>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ChatMessage
